import {
  AuthorityType,
  DocumentContext,
  DocumentEntityType,
  DocumentType,
  PAYOUT_CREDITOR_DOCUMENT_TYPE_BY_AUTHORITY
} from '@sollapay/enums';
import {Alert} from '@sollapay/ui/components';
import {useTranslation} from 'react-i18next';

import {DocumentUploadSection} from '@/features/documents';

import type {PayeeKind} from '../utils/creditor-account.utils';

const PREFIX = 'view.payouts.create.details';

const DOCUMENT_COPY_BY_PAYEE_KIND: Record<
  Exclude<PayeeKind, 'none'>,
  {titleKey: string; descriptionKey: string; documentType: DocumentType}
> = {
  beneficiary: {
    titleKey: 'confirmationDocument',
    descriptionKey: 'confirmationDocumentDescription',
    documentType: DocumentType.BANK_ACCOUNT_CONFIRMATION
  },
  [AuthorityType.MUNICIPALITY]: {
    titleKey: 'paymentDemandDocument',
    descriptionKey: 'paymentDemandDocumentDescription',
    documentType: PAYOUT_CREDITOR_DOCUMENT_TYPE_BY_AUTHORITY[AuthorityType.MUNICIPALITY]
  },
  [AuthorityType.ISRAEL_LAND_AUTHORITY]: {
    titleKey: 'paymentDemandDocument',
    descriptionKey: 'paymentDemandDocumentDescription',
    documentType: PAYOUT_CREDITOR_DOCUMENT_TYPE_BY_AUTHORITY[AuthorityType.ISRAEL_LAND_AUTHORITY]
  },
  [AuthorityType.TAX_AUTHORITY]: {
    titleKey: 'paymentVoucherDocument',
    descriptionKey: 'paymentVoucherDocumentDescription',
    documentType: PAYOUT_CREDITOR_DOCUMENT_TYPE_BY_AUTHORITY[AuthorityType.TAX_AUTHORITY]
  }
};

export interface PayeeDocumentSectionProps {
  payeeKind: PayeeKind;
  trustAccountId: string;
  metadata: Record<string, string | undefined>;
  metadataFilter: Record<string, string | undefined>;
  hasLinkedCreditorDocument: boolean;
  error: string | undefined;
  onDocumentUploaded: (documentId: string) => void;
  onDocumentsLoaded: (documentIds: string[]) => void;
}

/**
 * Before a payee is picked, shows a placeholder note explaining which document each payee kind
 * needs. Once one is picked, shows the upload card for that kind's document — only the
 * beneficiary flow reuses a document linked from a prior payout; authority documents (a
 * demand/voucher) are always specific to this payout.
 */
export function PayeeDocumentSection({
  payeeKind,
  trustAccountId,
  metadata,
  metadataFilter,
  hasLinkedCreditorDocument,
  error,
  onDocumentUploaded,
  onDocumentsLoaded
}: PayeeDocumentSectionProps) {
  const {t} = useTranslation('trustAccounts');

  if (payeeKind === 'none') {
    return (
      <Alert
        variant="info"
        title={t(`${PREFIX}.documentPlaceholderNoteTitle`)}
        description={t(`${PREFIX}.documentPlaceholderNoteBody`)}
        descriptionColor="secondary"
      />
    );
  }

  const {titleKey, descriptionKey, documentType} = DOCUMENT_COPY_BY_PAYEE_KIND[payeeKind];

  return (
    <DocumentUploadSection
      title={t(`${PREFIX}.${titleKey}`)}
      description={t(`${PREFIX}.${descriptionKey}`)}
      required
      maxFiles={1}
      entityType={DocumentEntityType.TRUST_ACCOUNT}
      entityId={trustAccountId}
      documentContext={DocumentContext.PAYOUTS}
      documentType={documentType}
      metadata={metadata}
      metadataFilter={payeeKind === 'beneficiary' ? metadataFilter : metadata}
      readOnly={payeeKind === 'beneficiary' && hasLinkedCreditorDocument}
      error={error}
      onDocumentUploaded={onDocumentUploaded}
      onDocumentsLoaded={onDocumentsLoaded}
    />
  );
}
